import AiGenConsts from './AiGenConsts';
import { post } from '../../Services/ApiService';
import * as toastActions from '../Toast/ToastActions';
import * as loadingActions from '../Loading/LoadingActions';

/**
 * 1) Fetch images from backend
 * 2) Create one "pollData" group (the first group) which may include RANDOM votes if missing
 * 3) Keep the rest of the groups in "votesData" with real votes
 */
export const fetchImages = () => async (dispatch) => {
    try {
        dispatch(loadingActions.setLoading('Fetching images...'));

        const response = await post('aigen/images', {});
        const content = response.data;

        if (content && Array.isArray(content.data)) {
            // Group data by (title + imageName)
            const groupedData = content.data.reduce((acc, { title, name, version, imageUrl, votes }) => {
                const groupKey = `${title}_${name}`;
                if (!acc[groupKey]) {
                    acc[groupKey] = { title, name: name, choices: [] };
                }
                acc[groupKey].choices.push({ version, imageUrl, votes });
                return acc;
            }, {});

            // Convert to array and sort each group's choices by version
            const groupedArray = Object.values(groupedData).map(group => ({
                ...group,
                choices: group.choices.sort((a, b) => a.version - b.version),
            }));

            // The first group => pollData (fill random votes if missing)
            // The rest => votesData (real votes from the server)
            const [firstGroup, ...restGroups] = groupedArray;

            if (!firstGroup) {
                // If no group found
                dispatch({
                    type: AiGenConsts.FETCH_IMAGES_SUCCESS,
                    payload: {
                        pollData: [],
                        votesData: [],
                    },
                });
            } else {
                const pollDataWithRandom = {
                    ...firstGroup,
                    choices: firstGroup.choices.map((choice) => ({
                        ...choice,
                        votes: choice.votes || Math.floor(Math.random() * 500) + 1,
                    })),
                };

                dispatch({
                    type: AiGenConsts.FETCH_IMAGES_SUCCESS,
                    payload: {
                        pollData: pollDataWithRandom,
                        votesData: restGroups,
                    },
                });
            }
        } else {
            // If invalid or empty data, just dispatch success with empty arrays
            dispatch({
                type: AiGenConsts.FETCH_IMAGES_SUCCESS,
                payload: {
                    pollData: [],
                    votesData: [],
                },
            });
        }
    } catch (error) {
        console.error('Failed to fetch images:', error);
        // We do NOT show a toast error here. 
        // We only log and update Redux state to avoid showing a toaster error.
        dispatch({
            type: AiGenConsts.FETCH_IMAGES_FAILURE,
            payload: error.message,
        });
    } finally {
        dispatch(loadingActions.disableLoading());
    }
};

/**
 * Let user set their choice for the first group in Redux
 * so that we can submit it alongside their email.
 */
export const setFirstGroupChoice = (choiceData) => {
    return {
        type: AiGenConsts.SET_FIRST_GROUP_CHOICE,
        payload: choiceData,
    };
};

/**
 * Submit votes (including user's choice for first group, if any)
 */
export const submitVotes = (email, votesFromPollScreen) => async (dispatch, getState) => {
    try {
        dispatch(loadingActions.setLoading('Submitting votes...'));

        // Retrieve the first group choice from Redux (if any)
        const { firstGroupUserChoice } = getState().aiGen;

        // Merge the first group choice with any additional votes from poll screen
        let finalVotes = [...votesFromPollScreen];

        if (firstGroupUserChoice) {
            finalVotes.push(firstGroupUserChoice);
        }

        // Generate a random email if not provided
        const isAnonymous = !email; // Check if the email is empty
        const emailToUse = email || `anon_${Date.now()}@anonymous.com`;

        //step 1 - send email to Zoho CRM as a lead (always, even if anonymous)
        try {
            const namePart = emailToUse.split('@')[0];
            const cleanName = namePart.startsWith('anon_') ? 'ANONYMOUS' : namePart.replace(/[^a-zA-Z0-9]/g, ' ').trim();
            await post('zoho/lead', {email: emailToUse,firstName: cleanName,lastName: '',company: 'POLL_CAMPAIGN',leadSource: 'POLL_SCREEN',phone: '+1234567890'});
        } catch (zohoErr) {
            console.log('Zoho lead creation failed:', zohoErr.message);
        }
        const payload = { email: emailToUse, votes: finalVotes };

        const response = await post('aigen/vote', payload);

        if (response.data && response.data.status) {
            dispatch({ type: AiGenConsts.VOTE_SUCCESS });

            // Only show success toast if the email is not anonymous
            if (!isAnonymous) {
                dispatch(toastActions.setToast('success', 'Votes submitted successfully!'));
            }
        } else {
            throw new Error(response.data?.message || 'Vote submission failed');
        }
    } catch (error) {
        console.error('Failed to submit votes:', error);
        dispatch({
            type: AiGenConsts.VOTE_FAILURE,
            payload: error.message,
        });
        dispatch(toastActions.setToast('error', 'Failed to submit votes.'));
    } finally {
        dispatch(loadingActions.disableLoading());
    }
};